/**
 * Confidence semantics — mirrors ai/prompt_manager.py's daily_briefing
 * prompt contract exactly (90-100 confirmed, 70-89 strong indication,
 * 40-69 possible hypothesis, 0-39 unknown). This is display-only: the
 * label itself always comes from the stored `confidence` string (the
 * model's own word), never re-derived from the score — this function
 * only maps a KNOWN label to its Albanian display text/tone for the
 * badge (see copy/sq.ts's confidenceLabel — wording changes there,
 * semantics stay exactly as documented above).
 *
 * Deliberately distinct from Story Score (see ui/README.md's Repository
 * Audit — Story Score is not present anywhere in Briefing.content_json
 * today, so this file has nothing to do with it).
 */

import { confidenceLabel } from "../copy/sq";

export type ConfidenceLabel = "confirmed" | "strong_indication" | "possible_hypothesis" | "unknown";

const CONFIDENCE_DISPLAY: Record<ConfidenceLabel, { text: string; tone: "good" | "warn" | "bad" | "neutral" }> = {
  confirmed: { text: confidenceLabel.confirmed, tone: "good" },
  strong_indication: { text: confidenceLabel.strong_indication, tone: "good" },
  possible_hypothesis: { text: confidenceLabel.possible_hypothesis, tone: "warn" },
  unknown: { text: confidenceLabel.unknown, tone: "neutral" },
};

export function normalizeConfidenceLabel(value: string | null | undefined): ConfidenceLabel | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "_");
  if (normalized in CONFIDENCE_DISPLAY) return normalized as ConfidenceLabel;
  return null;
}

export function confidenceDisplay(value: string | null | undefined) {
  const label = normalizeConfidenceLabel(value);
  if (!label) return null;
  return { label, ...CONFIDENCE_DISPLAY[label] };
}
